// OnThisPage: the "On this page" menu (the table of contents of m3.material.io): a small label, an optional title and text links to
// the parts of the page; the part in view is outlined (scroll spy). TocLayout sets it beside the content: a sticky side column on
// wide screens, a row of chips above the content on compact ones.
// Used by the `toc` content block (CaseStudyPage: the links are the page's headings) and the Admin Console's Components gallery.
//   OnThisPage { items: [{ id, label, level? }], title?, label = 'On this page', active? (controlled), onGo?(id) }
//   TocLayout  { toc: <OnThisPage …/>, side: 'left' | 'right', children }
//   window.OnThisPageNav = { scrollTo(id, smooth), useScrollSpy(ids) }
(function () {
  const { Box, Typography } = MaterialUI;
  const { useState, useEffect } = React;
  const SPY_LINE = 160; // px from the top of the window: the last part whose top passed this line is "in view"

  function scrollTo(id, smooth = true) {
    const el = document.getElementById(id);
    if (!el) return;
    const reduced = window.M3Core && window.M3Core.reducedMotion && window.M3Core.reducedMotion();
    el.scrollIntoView({ behavior: smooth && !reduced ? 'smooth' : 'auto', block: 'start' });
  }

  function useScrollSpy(ids) {
    const [active, setActive] = useState(ids[0] || null);
    const key = ids.join('|');
    useEffect(() => {
      if (!ids.length) return undefined;
      let raf = 0;
      const measure = () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          let cur = ids[0];
          for (const id of ids) { const el = document.getElementById(id); if (el && el.getBoundingClientRect().top < SPY_LINE) cur = id; }
          // at the very bottom the last part counts as in view even if it is too short to reach the line
          if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2) cur = ids[ids.length - 1];
          setActive(cur);
        });
      };
      measure();
      window.addEventListener('scroll', measure, { passive: true });
      window.addEventListener('resize', measure);
      return () => { window.removeEventListener('scroll', measure); window.removeEventListener('resize', measure); cancelAnimationFrame(raf); };
    }, [key]);
    return active;
  }

  function OnThisPage({ items = [], title, label = 'On this page', active: controlled, onGo }) {
    const { Chip } = window.Components;
    const compact = window.useCompactLayout();
    const spied = useScrollSpy(controlled === undefined ? items.map(x => x.id) : []);
    const active = controlled === undefined ? spied : controlled;
    const go = id => { if (onGo) onGo(id); else scrollTo(id); };
    if (!items.length) return null;
    if (compact) {
      return (
        <Box component="nav" aria-label={title ? `${label}: ${title}` : label} sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1, mb: 2, mx: -0.5, px: 0.5 }}>
          {items.map(x => <Chip key={x.id} label={x.label} selected={active === x.id} onClick={() => go(x.id)} sx={{ flex: '0 0 auto' }} />)}
        </Box>
      );
    }
    return (
      <Box component="nav" aria-label={title ? `${label}: ${title}` : label}>
        <Typography variant="overline" component="p" sx={{ display: 'block', textTransform: 'none', letterSpacing: '0.1px', color: 'var(--m3-on-surface-variant)', mb: 0.5, px: 2 }}>{label}</Typography>
        {title && <Typography variant="h5" component="p" sx={{ color: 'var(--m3-on-surface)', mb: 1.5, px: 2 }}>{title}</Typography>}
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {items.map(x => {
            const on = active === x.id;
            return (
              <Box component="li" key={x.id}>
                <Box component="a" href={`#${x.id}`} aria-current={on ? 'location' : undefined}
                  onClick={e => { e.preventDefault(); go(x.id); }}
                  sx={{
                    display: 'block', py: 1, pr: 2, pl: x.level === 3 ? 3.5 : 2, borderRadius: 'var(--m3-shape-large, 16px)', textDecoration: 'none',
                    border: '1px solid', borderColor: on ? 'var(--m3-outline)' : 'transparent',
                    color: on ? 'var(--m3-on-surface)' : 'var(--m3-on-surface-variant)', fontWeight: on ? 600 : 400,
                    fontSize: x.level === 3 ? '0.8125rem' : '0.875rem', lineHeight: 1.45,
                    transition: 'background-color var(--m3-duration-short) var(--m3-easing-standard), border-color var(--m3-duration-short) var(--m3-easing-standard)',
                    '&:hover': { bgcolor: 'color-mix(in srgb, var(--m3-on-surface) 8%, transparent)' },
                    '&:focus-visible': { outline: '2px solid var(--m3-primary)', outlineOffset: 2 }
                  }}>
                  {x.label}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  function TocLayout({ toc, side = 'right', children }) {
    const compact = window.useCompactLayout();
    if (!toc) return <>{children}</>;
    if (compact) return <>{toc}{children}</>;
    const column = <Box sx={{ flex: '0 0 240px', width: 240, position: 'sticky', top: 96, maxHeight: 'calc(100vh - 112px)', overflowY: 'auto', order: side === 'left' ? 0 : 2 }}>{toc}</Box>;
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
        {column}
        <Box sx={{ flex: 1, minWidth: 0, order: 1 }}>{children}</Box>
      </Box>
    );
  }

  window.OnThisPageNav = { scrollTo, useScrollSpy };
  window.Components = window.Components || {};
  window.Components.OnThisPage = OnThisPage;
  window.Components.TocLayout = TocLayout;
})();
